import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { formatMoney } from '../utils/api';

export default function NewSale() {
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [customerId, setCustomerId] = useState('');
  const [dollarRate, setDollarRate] = useState('');
  const [saleDate, setSaleDate] = useState(new Date().toISOString().split('T')[0]);
  const [note, setNote] = useState('');
  const [items, setItems] = useState([{ product_id: '', quantity: '', price_per_unit: '', total_price: 0 }]);
  const [paidAmount, setPaidAmount] = useState('');
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/customers').then(r => setCustomers(r.data));
    api.get('/products').then(r => setProducts(r.data));
    api.get('/settings').then(r => {
      const rate = r.data?.find?.(s => s.key_name === 'dollar_rate');
      if (rate) setDollarRate(rate.value);
    }).catch(() => {});
  }, []);

  const addItem = () => setItems([...items, { product_id: '', quantity: '', price_per_unit: '', total_price: 0 }]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));

  const updateItem = (i, field, val) => {
    const updated = items.map((item, idx) => {
      if (idx !== i) return item;
      const newItem = { ...item, [field]: val };
      if (field === 'product_id') {
        const p = products.find(p => p.id === Number(val));
        if (p) newItem.price_per_unit = p.price_sell;
      }
      newItem.total_price = Number(newItem.quantity || 0) * Number(newItem.price_per_unit || 0);
      return newItem;
    });
    setItems(updated);
  };

  const total = items.reduce((s, i) => s + Number(i.total_price || 0), 0);
  const debt = total - Number(paidAmount || 0);

  const handleSubmit = async () => {
    if (items.some(i => !i.product_id || !i.quantity)) return setMsg('لطفاً همه آیتم‌ها را کامل کنید');
    setSaving(true);
    try {
      const { data } = await api.post('/sales', {
        customer_id: customerId || null,
        items: items.map(i => ({ product_id: Number(i.product_id), quantity: Number(i.quantity), price_per_unit: Number(i.price_per_unit), total_price: Number(i.total_price) })),
        total_amount: total,
        paid_amount: Number(paidAmount || 0),
        dollar_rate: dollarRate || null,
        note,
        sale_date: saleDate
      });
      navigate(`/sales/${data.id}`);
    } catch (err) {
      setMsg(err.response?.data?.message || 'خطا در ثبت فروش');
    }
    setSaving(false);
  };

  return (
    <div>
      <div className="page-header">
        <h1>➕ فروش جدید</h1>
        <button className="btn btn-outline" onClick={() => navigate('/sales')}>← برگشت</button>
      </div>

      {msg && <div className="alert alert-danger">{msg}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
        <div className="card">
          <h3 style={{ marginBottom: 16 }}>اطلاعات فروش</h3>
          <div className="form-group">
            <label className="form-label">مشتری (اختیاری)</label>
            <select className="form-control" value={customerId} onChange={e => setCustomerId(e.target.value)}>
              <option value="">— فروش نقدی (بدون مشتری) —</option>
              {customers.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name} — {c.phone}</option>)}
            </select>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label className="form-label">تاریخ فروش</label>
              <input className="form-control" type="date" value={saleDate} onChange={e => setSaleDate(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">نرخ دلار (افغانی)</label>
              <input className="form-control" type="number" value={dollarRate} onChange={e => setDollarRate(e.target.value)} placeholder="مثلاً ۷۵" />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">یادداشت</label>
            <textarea className="form-control" value={note} onChange={e => setNote(e.target.value)} rows={2} />
          </div>
        </div>

        <div className="card">
          <h3 style={{ marginBottom: 16 }}>خلاصه مالی</h3>
          <div style={{ background: 'var(--bg)', borderRadius: 8, padding: 16, marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
              <span style={{ color: 'var(--text-muted)' }}>مجموع فروش:</span>
              <strong style={{ fontSize: 18 }}>{formatMoney(total)}</strong>
            </div>
            {dollarRate && <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, fontSize: 13 }}>
              <span style={{ color: 'var(--text-muted)' }}>معادل دلار:</span>
              <span>${(total / Number(dollarRate)).toFixed(2)}</span>
            </div>}
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: debt > 0 ? 'var(--danger)' : 'var(--success)', fontWeight: 600 }}>
                  {debt > 0 ? 'باقی وام:' : '✓ تسویه کامل'}
                </span>
                {debt > 0 && <strong style={{ color: 'var(--danger)' }}>{formatMoney(debt)}</strong>}
              </div>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">مقدار پرداخت شده (افغانی)</label>
            <input className="form-control" type="number" value={paidAmount}
              onChange={e => setPaidAmount(e.target.value)} placeholder="۰ = تمام وام" />
          </div>
          {debt > 0 && !customerId && (
            <div className="alert alert-danger" style={{ fontSize: 12 }}>⚠️ برای ثبت وام، مشتری را انتخاب کنید</div>
          )}
        </div>
      </div>

      {/* آیتم‌های فروش */}
      <div className="card">
        <div className="card-header">
          <h3>اقلام فروش</h3>
          <button className="btn btn-outline btn-sm" onClick={addItem}>➕ افزودن قلم</button>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr><th>محصول</th><th>مقدار</th><th>قیمت فی واحد</th><th>جمع</th><th></th></tr>
            </thead>
            <tbody>
              {items.map((item, i) => {
                const prod = products.find(p => p.id === Number(item.product_id));
                return (
                  <tr key={i}>
                    <td style={{ minWidth: 200 }}>
                      <select className="form-control" value={item.product_id} onChange={e => updateItem(i, 'product_id', e.target.value)}>
                        <option value="">انتخاب محصول...</option>
                        {products.map(p => <option key={p.id} value={p.id}>{p.name} — موجودی: {Number(p.stock).toLocaleString('fa')} {p.unit}</option>)}
                      </select>
                    </td>
                    <td style={{ minWidth: 120 }}>
                      <input className="form-control" type="number" value={item.quantity} onChange={e => updateItem(i, 'quantity', e.target.value)} placeholder={prod?.unit || 'مقدار'} />
                    </td>
                    <td style={{ minWidth: 140 }}>
                      <input className="form-control" type="number" value={item.price_per_unit} onChange={e => updateItem(i, 'price_per_unit', e.target.value)} placeholder="افغانی" />
                    </td>
                    <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{formatMoney(item.total_price)}</td>
                    <td>
                      {items.length > 1 && <button className="btn btn-danger btn-sm" onClick={() => removeItem(i)}>🗑️</button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr style={{ background: 'var(--bg)' }}>
                <td colSpan={3} style={{ textAlign: 'left', padding: '12px 14px', fontWeight: 600 }}>مجموع:</td>
                <td style={{ padding: '12px 14px', fontWeight: 700, fontSize: 16, color: 'var(--primary)' }}>{formatMoney(total)}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 20 }}>
        <button className="btn btn-outline" onClick={() => navigate('/sales')}>انصراف</button>
        <button className="btn btn-primary" style={{ padding: '10px 32px', fontSize: 15 }} onClick={handleSubmit} disabled={saving}>
          {saving ? '⏳ در حال ثبت...' : '✅ ثبت فروش'}
        </button>
      </div>
    </div>
  );
}
